// SMS Analytics tab: SMS stats, filters, table
import React, { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { X } from 'lucide-react';
import { deleteSMS } from 'services/api/WorkflowServiceAPI';
import { formatSMSStatus, setDateTimes, downloadCsv, getDateRangeForRangeType } from '../utils';
import { SMSStatistic, BulkActions, SMSTable, SMSDetailsModal } from '../components';
import type { SMSLead } from '../components';
import { Button, Input, Pagination, Select } from 'common/components';
import { useGetPartyStages, useGetSMSData, useGetSmsStatistics } from 'services/hooks/queries';
import { SortDirection } from 'types/call.type';
import { SMSConversationState, SMSNodeStatus, SMSNodeSubStatus, SMSStatus, SMSSortField } from 'types/sms.type';
import type { DateRangeType, HandleDeleteCallsParams } from '../AgenticWorkflowDetail.types';
import type { AgenticWorkflowOutletContext } from './CallAnalyticsPage';

import styles from '../AgenticWorkflowDetail.module.scss';

const SMSAnalyticsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { openNotification, closeNotification } = useOutletContext<AgenticWorkflowOutletContext>();

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([new Date(), new Date()]);
    const [dateRangeType, setDateRangeType] = useState<DateRangeType>('today');
    const [filterLeadStatus, setFilterLeadStatus] = useState('');
    const [filterSMSStatus, setFilterSMSStatus] = useState('');
    const [filterSMSDirection, setFilterSMSDirection] = useState('');
    const [smsSortField, setSmsSortField] = useState<SMSSortField>('smsDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedSms, setSelectedSms] = useState<SMSLead | null>(null);
    const [showSmsDetailsModal, setShowSmsDetailsModal] = useState(false);
    const selectedStatusQueryParams = useMemo(() => {
        const baseParams = {
            smsStatus: '',
            nodeStatus: '',
            conversationState: '',
            nodeSubStatus: '',
        };
        if (!filterSMSStatus) return baseParams;

        if (filterSMSStatus in SMSStatus) return { ...baseParams, smsStatus: filterSMSStatus };
        if (filterSMSStatus in SMSNodeStatus) return { ...baseParams, nodeStatus: filterSMSStatus };
        if (filterSMSStatus in SMSConversationState) return { ...baseParams, conversationState: filterSMSStatus };
        if (filterSMSStatus in SMSNodeSubStatus) return { ...baseParams, nodeSubStatus: filterSMSStatus };

        return baseParams;
    }, [filterSMSStatus]);

    const debouncedSetSearchTerm = useCallback(
        debounce((value: string) => setSearchTerm(value), 300),
        []
    );

    useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

    const { startWithTime, endWithTime } = setDateTimes(dateRange);

    const smsQueryParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        ...selectedStatusQueryParams,
        partyStage: filterLeadStatus,
        search: searchTerm,
        perPage: rowsPerPage,
        sortOrder: sortDirection,
        sortBy: smsSortField,
        currentPage,
    };

    const smsStatisticsParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        partyStage: filterLeadStatus,
        smsStatus: filterSMSStatus in SMSStatus ? filterSMSStatus : '',
        search: searchTerm,
    };

    const { partyStages } = useGetPartyStages();
    const { smsStatistics, refetchSmsStatistics } = useGetSmsStatistics(smsStatisticsParams);
    const { smsData, isSMSDataFetching, refetch: refetchSmsData } = useGetSMSData(smsQueryParams, true);

    const smsLeads = useMemo<SMSLead[]>(() => {
        if (!smsData?.data?.length) return [];
        return smsData.data.map((sms) => {
            const smsDateTime = new Date(sms.sentTime || sms.scheduledDate);
            return {
                id: sms.crmId,
                smsId: sms.smsId,
                smsDate: smsDateTime.toLocaleDateString('en-US'),
                smsTime: smsDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                status: sms.nodeStatus || sms.conversationState || sms.status || sms.nodeSubStatus || 'QUEUED',
                crmStatus: sms.partyStage || 'NEW_LEAD',
                crmId: sms.crmId,
                name: sms.contactName || 'Unknown',
                number: sms.phoneNumber || 'Unknown',
                messageContent: sms.messageContent || '',
                messageDirection: sms.messageDirection,
                disposition: sms.disposition,
                deliveredTime: sms.deliveredTime,
            };
        });
    }, [smsData]);

    const filteredSmsLeads = useMemo<SMSLead[]>(() => {
        if (!filterSMSDirection) return smsLeads;
        return smsLeads.filter((sms) => sms.messageDirection === filterSMSDirection);
    }, [smsLeads, filterSMSDirection]);

    const totalPages = smsData?.pagination?.totalPages || 1;
    const totalRecords = smsData?.pagination?.totalRecords || 0;
    const currentPageFromAPI = smsData?.pagination?.currentPage || currentPage;
    const perPageFromAPI = smsData?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        const paginationData = smsData?.pagination;
        if (paginationData?.currentPage) setCurrentPage(paginationData.currentPage);
        if (paginationData?.perPage) setRowsPerPage(paginationData.perPage);
    }, [smsData?.pagination]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setSearchTerm('');
        setFilterLeadStatus('');
        setFilterSMSStatus('');
        setFilterSMSDirection('');
        setDateRangeType('today');
        setDateRange([new Date(), new Date()]);
        setCurrentPage(1);
        setSelectedCalls([]);
    };

    const handleDateRangeChange = (range: string) => {
        const newRange = getDateRangeForRangeType(range);
        setDateRangeType(range as DateRangeType);
        if (newRange) {
            setCurrentPage(1);
            setDateRange(newRange);
        }
    };

    const handleSortChange = (field: SMSSortField, newSortDirection: SortDirection) => {
        setSmsSortField(field);
        setSortDirection(newSortDirection);
    };

    const handleCheckboxChange = (smsId: string) => {
        setSelectedCalls((prev) => (prev.includes(smsId) ? prev.filter((id) => id !== smsId) : [...prev, smsId]));
    };

    const handleSelectAllCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelectedCalls(filteredSmsLeads.map((s) => s.smsId));
        } else {
            const pageIds = filteredSmsLeads.map((s) => s.smsId);
            setSelectedCalls(selectedCalls.filter((id) => !pageIds.includes(id)));
        }
    };

    const handleDeleteSMS = async ({ selectedCallId }: HandleDeleteCallsParams) => {
        if (!selectedCallId && !selectedCalls.length) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one SMS to delete.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        openNotification({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${selectedCallId ? 'this SMS' : `${selectedCalls.length} SMS message(s)`}?`,
            type: 'error',
            body: null,
            buttons: [
                { label: 'Cancel', onClick: closeNotification, style: { backgroundColor: '#9b9b9b' } },
                {
                    label: 'Delete',
                    onClick: async () => {
                        closeNotification();
                        try {
                            setIsDeleting(true);
                            const idsToDelete = selectedCallId ? [selectedCallId] : selectedCalls;
                            await deleteSMS(idsToDelete);
                            openNotification({
                                isOpen: true,
                                title: 'Deletion Successful',
                                message: 'SMS messages deleted successfully',
                                type: 'success',
                                body: null,
                                buttons: [],
                            });
                            await refetchSmsStatistics();
                            await refetchSmsData();
                            setSelectedCalls([]);
                        } catch (err) {
                            console.error('Error deleting SMS:', err);
                            openNotification({
                                isOpen: true,
                                title: 'Error',
                                message: `Failed to delete SMS: ${(err as Error).message || 'Unknown error'}`,
                                type: 'error',
                                body: null,
                                buttons: [],
                            });
                        } finally {
                            setIsDeleting(false);
                        }
                    },
                    style: { backgroundColor: '#f44336' },
                },
            ],
        });
    };

    const handleExportCSV = (params?: { selectedCallId?: string }) => {
        const { selectedCallId } = params || {};
        if (!selectedCalls.length && !selectedCallId) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one SMS to export.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        const selectedLeads = selectedCallId
            ? filteredSmsLeads.filter((s) => s.smsId === selectedCallId)
            : filteredSmsLeads.filter((s) => selectedCalls.includes(s.smsId));
        const headers = [
            'SMS ID',
            'SMS Date',
            'SMS Time',
            'Status',
            'Party Status',
            'CRM ID',
            'Name',
            'Number',
            'Message',
        ];
        const csvRows = selectedLeads.map((s) => [
            s.smsId,
            s.smsDate,
            s.smsTime,
            s.status,
            s.crmStatus,
            s.crmId,
            s.name,
            s.number,
            s.messageContent || '',
        ]);
        const dateString = new Date().toISOString().split('T')[0];
        downloadCsv(headers, csvRows, `workflow_sms_${dateString}.csv`);
        openNotification({
            isOpen: true,
            title: 'Export Complete',
            message: `Successfully exported ${selectedLeads.length} SMS message(s) to CSV.`,
            type: 'success',
            body: null,
            buttons: [],
        });
        if (!selectedCallId) setSelectedCalls([]);
    };

    const handleSmsDetails = (record: SMSLead) => {
        setSelectedSms(record);
        setShowSmsDetailsModal(true);
    };

    const closeSmsDetailsModal = () => {
        setShowSmsDetailsModal(false);
        setSelectedSms(null);
    };

    const smsStatusOptions = useMemo(() => {
        return [
            ...Object.keys(SMSNodeStatus).map((status) => ({
                label: formatSMSStatus(status),
                value: status,
                category: 'Node Status',
            })),
            ...Object.keys(SMSConversationState).map((status) => ({
                label: formatSMSStatus(status),
                value: status,
                category: 'Conversation State',
            })),
            ...Object.keys(SMSStatus).map((status) => ({
                label: formatSMSStatus(status),
                value: status,
                category: 'SMS Status',
            })),
            ...Object.keys(SMSNodeSubStatus).map((status) => ({
                label: formatSMSStatus(status),
                value: status,
                category: 'Node Sub Status',
            })),
        ];
    }, []);

    return (
        <>
            <SMSStatistic smsStats={smsStatistics} />

            <div className={styles.workflowLeadsContainer}>
                <div className={styles.filterSection}>
                    <div className={styles.searchBox}>
                        <Input
                            type='search'
                            placeholder='Search by name, number or ID'
                            onChange={(e) => {
                                if (e.target.value.length > 1 || e.target.value.length === 0) {
                                    debouncedSetSearchTerm(e.target.value);
                                }
                            }}
                        />
                    </div>
                    <div className={styles.callDirectionFilter}>
                        <Select
                            value={filterSMSDirection}
                            onChange={(e) => {
                                setFilterSMSDirection(e.target.value);
                                setCurrentPage(1);
                            }}
                            options={[
                                { value: '', label: 'All SMS Directions' },
                                { value: 'OUTBOUND', label: 'Outbound SMS' },
                                { value: 'INBOUND', label: 'Inbound SMS' },
                            ]}
                        />
                    </div>
                    <Button
                        variant='outline-primary'
                        icon={X}
                        onClick={handleClearFilter}
                        size='sm'
                        className={styles.clearFilterBtn}
                    >
                        Clear Filters
                    </Button>
                </div>
                <div className={styles.workflowLeadsSection}>
                    <SMSTable
                        data={filteredSmsLeads}
                        selectedIds={selectedCalls}
                        sortField={smsSortField}
                        sortDirection={sortDirection}
                        filterLeadStatus={filterLeadStatus}
                        filterSMSStatus={filterSMSStatus}
                        partyStages={partyStages?.data ?? []}
                        smsStatusOptions={smsStatusOptions}
                        onSortChange={handleSortChange}
                        onFilterLeadStatus={(value) => {
                            setFilterLeadStatus(value);
                            setCurrentPage(1);
                        }}
                        onFilterSMSStatus={(value) => {
                            setFilterSMSStatus(value);
                            setCurrentPage(1);
                        }}
                        onSelectAllCheckboxChange={handleSelectAllCheckboxChange}
                        onCheckboxChange={handleCheckboxChange}
                        onSmsDetails={handleSmsDetails}
                        dateRangeConfig={{
                            type: dateRangeType,
                            onTypeChange: handleDateRangeChange,
                            setDateRange,
                        }}
                        isLoading={isSMSDataFetching}
                        hasActiveFilters={
                            !!(
                                searchTerm.trim() ||
                                filterLeadStatus ||
                                filterSMSStatus ||
                                filterSMSDirection ||
                                dateRangeType !== 'today'
                            )
                        }
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <BulkActions
                            selectedCalls={selectedCalls}
                            handleDeleteCalls={handleDeleteSMS}
                            handleExportCSV={handleExportCSV}
                        />
                        <Pagination
                            currentPage={currentPageFromAPI}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            rowsPerPage={perPageFromAPI}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className={styles.agenticPagination}
                        />
                    </div>
                </div>
            </div>

            <SMSDetailsModal
                sms={selectedSms}
                isOpen={showSmsDetailsModal}
                onClose={closeSmsDetailsModal}
                onDelete={handleDeleteSMS}
                onExportCSV={handleExportCSV}
                isLoading={isSMSDataFetching}
            />
        </>
    );
};

export default SMSAnalyticsPage;
